import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Label,
  TextInput,
  Select,
  Textarea,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "flowbite-react";
import { subtractDays, toDatetimeLocalValue } from "../../utils/daysHandlers";

const AUCTION_TYPES = ["AUCTION", "OTC", "LAND_BANK", "COUNTY_HELD"];
const RESEARCH_STATUS_LABELS = {
  PENDING: "Pendente",
  READY: "Pronto p/ VA",
  IN_PROGRESS: "Em andamento",
  WAITING_ADMIN: "Aguardando Admin",
  APPROVED: "Aprovado",
  ABANDONED: "Abandonado",
};

const PAYMENT_STATUS_LABELS = {
  UNPAID: "Não pago",
  PAID: "Pago",
};

export default function AuctionFormModal({
  open,
  onClose,
  initialData,
  saving,
  onSubmit,
  states = [],
  counties = [],
  ruleTemplates = [],
  users = [],
  currentUserId,
}) {
  const isEdit = !!initialData?.id;
  const researchStatus = initialData?.research_status || "PENDING";
  const paymentStatus = initialData?.payment_status || "UNPAID";

  const canEditBase = !isEdit || ["PENDING", "READY"].includes(researchStatus);

  const canAssignVA = isEdit && ["PENDING", "READY"].includes(researchStatus);

  const canApprove = isEdit && researchStatus === "WAITING_ADMIN";

  const canPay = isEdit && researchStatus === "APPROVED";

  const defaults = useMemo(
    () => ({
      county_id: "",
      rule_template_id: "",
      auction_type: "AUCTION",
      starts_at: "",
      research_due_at: "", // <- novo
      list_source_url: "",
      notes: "",
      assigned_to_user_id: "",
    }),
    [],
  );

  const [form, setForm] = useState(defaults);
  const [touched, setTouched] = useState({});
  const [stateId, setStateId] = useState("");

  const filteredCounties = useMemo(() => {
    if (!stateId) return [];
    return (counties || []).filter((c) => c.state_id === stateId);
  }, [counties, stateId]);

  useEffect(() => {
    if (!open) return;

    if (!initialData) {
      setForm(defaults);
      setStateId("");
      setTouched({});
      return;
    }

    const selectedCounty = counties.find((c) => c.id === initialData.county_id);
    setStateId(selectedCounty?.state_id || "");

    setForm({
      county_id: initialData.county_id ?? "",
      rule_template_id: initialData.rule_template_id ?? "",
      auction_type: initialData.auction_type ?? "AUCTION",
      starts_at: toDatetimeLocalValue(initialData.starts_at),
      research_due_at: toDatetimeLocalValue(initialData.research_due_at), // <- novo
      list_source_url: initialData.list_source_url ?? "",
      notes: initialData.notes ?? "",
      assigned_to_user_id: initialData.assigned_to_user_id ?? "",
    });
    setTouched({});
  }, [open, initialData, defaults, counties]);

  const errors = {
    county_id: !form.county_id ? "Obrigatório" : null,
    rule_template_id: !form.rule_template_id ? "Obrigatório" : null,
    auction_type: !form.auction_type ? "Obrigatório" : null,
    starts_at: !form.starts_at ? "Obrigatório" : null,
    // sampled_by_admin_id vem do auth
  };

  const hasErrors = !!(
    errors.county_id ||
    errors.rule_template_id ||
    errors.auction_type ||
    errors.starts_at
  );

  function setField(key, value) {
    setForm((s) => ({ ...s, [key]: value }));
  }

  function markTouched(key) {
    setTouched((t) => ({ ...t, [key]: true }));
  }

  function handleSave() {
    setTouched({
      county_id: true,
      rule_template_id: true,
      auction_type: true,
      starts_at: true,
    });
    if (hasErrors) return;

    const payload = {
      county_id: form.county_id,
      rule_template_id: form.rule_template_id,
      auction_type: form.auction_type,
      starts_at: new Date(form.starts_at).toISOString(),
      research_due_at: form.research_due_at
        ? new Date(form.research_due_at).toISOString()
        : null, // <- aceita null
      sampled_by_admin_id: currentUserId || null,
      list_source_url: form.list_source_url?.trim()
        ? form.list_source_url.trim()
        : null,
      notes: form.notes?.trim() ? form.notes.trim() : null,
    };

    onSubmit(payload);
  }

  return (
    <Modal show={open} onClose={onClose}>
      <ModalHeader>{isEdit ? "Editar Auction" : "Novo Auction"}</ModalHeader>

      <ModalBody>
        <div className="space-y-4">
          <div>
            <Label htmlFor="state_id" value="State" />
            <Select
              id="state_id"
              value={stateId}
              onChange={(e) => {
                const next = e.target.value;
                setStateId(next);
                setField("county_id", ""); // reset county ao trocar state
              }}
              disabled={saving === true || !canEditBase}
            >
              <option value="">Selecione...</option>
              {states.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="county_id" value="County" />
            <Select
              id="county_id"
              value={form.county_id}
              onChange={(e) => setField("county_id", e.target.value)}
              onBlur={() => markTouched("county_id")}
              disabled={saving === true || !canEditBase || !stateId}
            >
              <option value="">
                {stateId ? "Selecione..." : "Selecione um State primeiro"}
              </option>

              {filteredCounties.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
            {touched.county_id && errors.county_id ? (
              <p className="mt-1 text-sm text-red-600">{errors.county_id}</p>
            ) : null}
          </div>

          <div>
            <Label htmlFor="rule_template_id" value="Rule Template" />
            <Select
              id="rule_template_id"
              value={form.rule_template_id}
              onChange={(e) => setField("rule_template_id", e.target.value)}
              onBlur={() => markTouched("rule_template_id")}
              disabled={saving === true || !canEditBase}
            >
              <option value="">Selecione...</option>
              {ruleTemplates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </Select>
            {touched.rule_template_id && errors.rule_template_id ? (
              <p className="mt-1 text-sm text-red-600">
                {errors.rule_template_id}
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="auction_type">Auction Type</Label>
              <Select
                id="auction_type"
                value={form.auction_type}
                onChange={(e) => setField("auction_type", e.target.value)}
                onBlur={() => markTouched("auction_type")}
                disabled={saving === true || !canEditBase}
              >
                {AUCTION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
              {touched.auction_type && errors.auction_type ? (
                <p className="mt-1 text-sm text-red-600">
                  {errors.auction_type}
                </p>
              ) : null}
            </div>

            <div>
              <Label htmlFor="starts_at">Starts At</Label>
              <TextInput
                id="starts_at"
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) => {
                  const nextStarts = e.target.value;
                  setField("starts_at", nextStarts);

                  // auto-sugere due_at = starts_at - 3 dias, mas só se user não mexeu no due ainda
                  const dueWasTouched = !!touched.research_due_at;
                  if (!dueWasTouched) {
                    setField(
                      "research_due_at",
                      nextStarts ? subtractDays(nextStarts, 3) : "",
                    );
                  }
                }}
                onBlur={() => markTouched("starts_at")}
                disabled={saving === true || !canEditBase}
                color={
                  touched.starts_at && errors.starts_at ? "failure" : "gray"
                }
                helperText={
                  touched.starts_at && errors.starts_at
                    ? errors.starts_at
                    : null
                }
              />
            </div>
          </div>

          <div>
            <Label htmlFor="research_due_at">Deadline</Label>
            <TextInput
              id="research_due_at"
              type="datetime-local"
              value={form.research_due_at}
              onChange={(e) => setField("research_due_at", e.target.value)}
              onBlur={() => markTouched("research_due_at")}
              disabled={saving === true || !canEditBase}
              helperText="Sugestão automática: 3 dias antes do leilão (editável)."
            />
          </div>

          <div>
            <Label
              htmlFor="list_source_url"
              value="List Source URL (opcional)"
            />
            <TextInput
              id="list_source_url"
              value={form.list_source_url}
              onChange={(e) => setField("list_source_url", e.target.value)}
              placeholder="https://..."
              disabled={saving === true || !canEditBase}
            />
          </div>

          <div>
            <Label htmlFor="notes" value="Notes (opcional)" />
            <Textarea
              id="notes"
              rows={4}
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder="Observações do leilão..."
              disabled={saving === true || !canEditBase}
            />
          </div>
        </div>
        {isEdit && (
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Governança
            </h3>

            {/* Status atual */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label value="Status do Leilão" />
                <p className="text-sm">
                  {RESEARCH_STATUS_LABELS[researchStatus]}
                </p>
              </div>

              <div>
                <Label value="Pagamento" />
                <p className="text-sm">
                  {PAYMENT_STATUS_LABELS[paymentStatus]}
                </p>
              </div>
            </div>

            {/* Atribuição de VA */}
            {canAssignVA && (
              <div>
                <Label value="Atribuir VA" />
                <Select
                  value={form.assigned_to_user_id}
                  onChange={(e) =>
                    setField("assigned_to_user_id", e.target.value)
                  }
                >
                  <option value="">Selecione...</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name || u.email}
                    </option>
                  ))}
                </Select>

                <Button
                  className="mt-2 text-slate-800 bg-slate-200"
                  size="sm"
                  onClick={() =>
                    onSubmit({
                      assigned_to_user_id: form.assigned_to_user_id,
                      research_status: "IN_PROGRESS",
                      assigned_at: new Date().toISOString(),
                    })
                  }
                  disabled={!form.assigned_to_user_id}
                >
                  Liberar para VA
                </Button>
              </div>
            )}

            {/* Aprovação */}
            {canApprove && (
              <Button
                color="success"
                size="sm"
                onClick={() =>
                  onSubmit({
                    research_status: "APPROVED",
                    approved_by_admin_id: currentUserId,
                    approved_at: new Date().toISOString(),
                  })
                }
              >
                Aprovar Leilão
              </Button>
            )}

            {/* Pagamento */}
            {canPay && (
              <Button
                color="success"
                size="sm"
                onClick={() =>
                  onSubmit({
                    payment_status: "PAID",
                    paid_by_admin_id: currentUserId,
                    paid_at: new Date().toISOString(),
                  })
                }
              >
                Marcar como Pago
              </Button>
            )}
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button
          className="text-slate-600"
          onClick={handleSave}
          disabled={saving === true || !canEditBase}
        >
          {saving === true ? "Salvando..." : "Salvar"}
        </Button>
        <Button color="gray" onClick={onClose} disabled={saving === true}>
          Cancelar
        </Button>
      </ModalFooter>
    </Modal>
  );
}
